var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
var __generator = (this && this.__generator) || function (thisArg, body) {
    var _ = { label: 0, sent: function() { if (t[0] & 1) throw t[1]; return t[1]; }, trys: [], ops: [] }, f, y, t, g = Object.create((typeof Iterator === "function" ? Iterator : Object).prototype);
    return g.next = verb(0), g["throw"] = verb(1), g["return"] = verb(2), typeof Symbol === "function" && (g[Symbol.iterator] = function() { return this; }), g;
    function verb(n) { return function (v) { return step([n, v]); }; }
    function step(op) {
        if (f) throw new TypeError("Generator is already executing.");
        while (g && (g = 0, op[0] && (_ = 0)), _) try {
            if (f = 1, y && (t = op[0] & 2 ? y["return"] : op[0] ? y["throw"] || ((t = y["return"]) && t.call(y), 0) : y.next) && !(t = t.call(y, op[1])).done) return t;
            if (y = 0, t) op = [op[0] & 2, t.value];
            switch (op[0]) {
                case 0: case 1: t = op; break;
                case 4: _.label++; return { value: op[1], done: false };
                case 5: _.label++; y = op[1]; op = [0]; continue;
                case 7: op = _.ops.pop(); _.trys.pop(); continue;
                default:
                    if (!(t = _.trys, t = t.length > 0 && t[t.length - 1]) && (op[0] === 6 || op[0] === 2)) { _ = 0; continue; }
                    if (op[0] === 3 && (!t || (op[1] > t[0] && op[1] < t[3]))) { _.label = op[1]; break; }
                    if (op[0] === 6 && _.label < t[1]) { _.label = t[1]; t = op; break; }
                    if (t && _.label < t[2]) { _.label = t[2]; _.ops.push(op); break; }
                    if (t[2]) _.ops.pop();
                    _.trys.pop(); continue;
            }
            op = body.call(thisArg, _);
        } catch (e) { op = [6, e]; y = 0; } finally { f = t = 0; }
        if (op[0] & 5) throw op[1]; return { value: op[0] ? op[1] : void 0, done: true };
    }
};
import React, { useState, useCallback, useEffect, useRef } from 'react';
import { FormattedMessage } from 'react-intl';
import { useQuery } from 'react-query';
import { useOkapiKy } from '@folio/stripes/core';
import { Accordion, Button, ButtonGroup, Loading, MessageBanner, } from '@folio/stripes/components';
import { getSkos } from '../../api/recordService';
var FORMATS = [
    { key: 'turtle', labelId: 'ui-czna-authorities.authorities.detail.skos.format.turtle' },
    { key: 'jsonld', labelId: 'ui-czna-authorities.authorities.detail.skos.format.jsonld' },
    { key: 'rdfxml', labelId: 'ui-czna-authorities.authorities.detail.skos.format.rdfxml' },
];
var MONO_FONT = "'SF Mono', 'Cascadia Code', 'Consolas', monospace";
var preStyle = {
    fontFamily: MONO_FONT,
    fontSize: '0.78rem',
    lineHeight: 1.5,
    background: '#f8fafc',
    border: '1px solid #cbd5e1',
    borderRadius: '6px',
    padding: '12px',
    margin: '8px 0 0',
    maxHeight: '480px',
    overflow: 'auto',
    whiteSpace: 'pre',
};
/**
 * CZNA-193 (TASK-150 step 1) — read-only SKOS linked-data viewer.
 *
 * Renders the current authority record as SKOS in one of three RDF syntaxes
 * (Turtle / JSON-LD / RDF-XML) via `GET /czna-authorities/records/{id}/skos`.
 * The backend does all the mapping; this panel only switches format, displays
 * the raw document, and offers a clipboard copy. The query is keyed by
 * `(id, format)` so each format is fetched lazily and cached.
 */
var SkosViewer = function (_a) {
    var id = _a.id;
    var ky = useOkapiKy();
    var _b = useState('turtle'), format = _b[0], setFormat = _b[1];
    var _c = useState(false), copied = _c[0], setCopied = _c[1];
    var copiedTimerRef = useRef(undefined);
    var _d = useQuery(['record-skos', id, format], function () { return getSkos(ky, id, format); }, { enabled: !!id, keepPreviousData: true }), data = _d.data, isLoading = _d.isLoading, isError = _d.isError;
    // Clear the "Copied" reset timer if the panel unmounts before it fires.
    useEffect(function () { return function () { return clearTimeout(copiedTimerRef.current); }; }, []);
    var handleCopy = useCallback(function () { return __awaiter(void 0, void 0, void 0, function () {
        var _a;
        return __generator(this, function (_b) {
            switch (_b.label) {
                case 0:
                    if (!data)
                        return [2 /*return*/];
                    _b.label = 1;
                case 1:
                    _b.trys.push([1, 3, , 4]);
                    return [4 /*yield*/, navigator.clipboard.writeText(data)];
                case 2:
                    _b.sent();
                    setCopied(true);
                    clearTimeout(copiedTimerRef.current);
                    copiedTimerRef.current = setTimeout(function () { return setCopied(false); }, 2000);
                    return [3 /*break*/, 4];
                case 3:
                    _a = _b.sent();
                    return [3 /*break*/, 4];
                case 4: return [2 /*return*/];
            }
        });
    }); }, [data]);
    return (React.createElement(Accordion, { label: React.createElement(FormattedMessage, { id: "ui-czna-authorities.authorities.detail.skos.label" }), closedByDefault: true },
        React.createElement("div", { style: { display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' } },
            React.createElement(ButtonGroup, { "data-testid": "skos-format-switcher" }, FORMATS.map(function (_a) {
                var key = _a.key, labelId = _a.labelId;
                return (React.createElement(Button, { key: key, "data-testid": "skos-format-".concat(key), buttonStyle: format === key ? 'primary' : 'default', onClick: function () { return setFormat(key); }, marginBottom0: true },
                    React.createElement(FormattedMessage, { id: labelId })));
            })),
            React.createElement(Button, { "data-testid": "skos-copy-button", buttonStyle: "default", onClick: handleCopy, disabled: !data, marginBottom0: true },
                React.createElement(FormattedMessage, { id: copied
                        ? 'ui-czna-authorities.authorities.detail.skos.copied'
                        : 'ui-czna-authorities.authorities.detail.skos.copy' }))),
        isLoading && React.createElement(Loading, null),
        isError && (React.createElement("div", { style: { marginTop: '8px' } },
            React.createElement(MessageBanner, { type: "error" },
                React.createElement(FormattedMessage, { id: "ui-czna-authorities.authorities.detail.skos.loadError" })))),
        !isLoading && !isError && data && (React.createElement("pre", { "data-testid": "skos-output", style: preStyle }, data))));
};
export default SkosViewer;
